import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { Modal } from "reactstrap";
import Constants from "common/Constants";
import AlertConfirmPopup from "components/common/AlertConfirmPopup";
import { callPost } from "utils/CommonApi";
import { convertDateToHypen, formatDateString, toExamDvsnCd } from "components/mypage/myExamInfo/examConstants";
import examPlanInfoImage from "styles/common/img/examplan/img_popup_info.webp";
import usexAPI from "store/customHooks/usexAPI";
import { xAPI_ID_VALUE } from "utils/xAPI_PATH";
import { EXAM_PLAN_XAPI_CONTENT } from "../examPlanXapi";
import "./ExamPlanIntroPopup.scss";

// SUMMARY(D-000일 안내) 화면은 이번 개편 대상이 아니라 사용하지 않는다. 항상 GUIDE 부터 시작한다.
const POPUP_STEP = { SUMMARY: 0, GUIDE: 1, PERIOD: 2 };
const WARNING_MESSAGE = {
    invalid: "*시험기간이 유효하지 않습니다.",
    overtime: "*시험기간은 한 달을 초과할 수 없습니다.",
    overlap: "*동일한 시험기간이 존재합니다.",
    nothing: "*시험기간을 설정해주세요.",
};

const toInputDate = (date) => (date ? convertDateToHypen(date) : "");
const isInteractiveTarget = (target) => Boolean(target?.closest?.("button, input, label, a"));
const getExamList = (res) => res?.RESP_RESULT?.EXAM_LIST || res?.RESP_RESULT?.RESP_RESULT?.EXAM_LIST || [];
const isSaveSuccessful = (res) =>
    res?.RESP_RESULT?.RESULT_MSG === "SUCCESS" || res?.RESP_RESULT?.RESP_RESULT?.RESULT_MSG === "SUCCESS";

export const validateExamPeriod = (startDate, endDate, examList = [], currentExamCode = "") => {
    if (!startDate && !endDate) return WARNING_MESSAGE.nothing;
    if (!startDate || !endDate) return WARNING_MESSAGE.invalid;

    const start = moment(startDate, "YYYY-MM-DD", true);
    const end = moment(endDate, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) return WARNING_MESSAGE.invalid;
    if (end.diff(start, "days") > 30) return WARNING_MESSAGE.overtime;

    const hasOverlap = examList
        .filter((exam) => exam.EXAM_DVSN_CD !== currentExamCode)
        .some((exam) => {
            if (!exam.EXAM_START_DT || !exam.EXAM_END_DT) return false;
            const examStart = moment(exam.EXAM_START_DT, ["YYYYMMDD", "YYYY-MM-DD"], true);
            const examEnd = moment(exam.EXAM_END_DT, ["YYYYMMDD", "YYYY-MM-DD"], true);
            return (
                examStart.isValid() &&
                examEnd.isValid() &&
                start.isSameOrBefore(examEnd) &&
                end.isSameOrAfter(examStart)
            );
        });

    return hasOverlap ? WARNING_MESSAGE.overlap : "";
};

export const createExamSchedulePayload = ({
    missPlanInfo,
    mmbrSeq,
    startDate,
    endDate,
    periodUnknown = false,
    noExam = false,
}) => ({
    REG_USER: mmbrSeq,
    EXAM_LIST: [
        {
            GRAD_CD: missPlanInfo.GRAD_CD,
            EXAM_DVSN_CD: toExamDvsnCd(missPlanInfo),
            EXAM_START_DT: periodUnknown || noExam ? "" : formatDateString(startDate),
            EXAM_END_DT: periodUnknown || noExam ? "" : formatDateString(endDate),
            EXAM_VLTN_YN: noExam ? "N" : "Y",
            /**
             * "시험기간을 몰라요" 는 기간만 미정일 뿐이라 마이페이지 일정 노출은 끄지 않는다.
             * 예전에는 여기서 N 으로 저장돼, 이후 시험정보 화면에서 일정을 등록해도
             * 미노출 체크가 켜진 채로 다시 저장되면서 일정이 계속 안 보였다.
             * (시험을 보지 않는 경우만 미노출로 둔다)
             */
            PLAN_DSP_YN: noExam ? "N" : "Y",
        },
    ],
});

// JOIN_YN 은 시험대비모드 참여 여부, EXAM_PERIOD_YN 은 시험기간 입력 여부로 서로 다른 값
// 시험기간을 몰라요 : 참여하되 기간만 미정 / 시험을 보지 않아요 : 미참여
const createMissionPlanPayload = ({ startDate, endDate, periodUnknown, noExam }) => ({
    JOIN_YN: noExam ? "N" : "Y",
    EXAM_PERIOD_YN: periodUnknown || noExam ? "N" : "Y",
    EXAM_START_DT: periodUnknown || noExam ? "" : formatDateString(startDate),
    EXAM_END_DT: periodUnknown || noExam ? "" : formatDateString(endDate),
});

const ExamPlanIntroPopup = ({ isOpen, missPlanInfo, userId, mmbrSeq, onClose, onCloseToday, onComplete }) => {
    const { ServiceMessage, ServiceUnit_B } = usexAPI();
    const [step, setStep] = useState(POPUP_STEP.GUIDE);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [periodUnknown, setPeriodUnknown] = useState(false);
    const [examList, setExamList] = useState([]);
    const [warningMessage, setWarningMessage] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const examCode = toExamDvsnCd(missPlanInfo);
    const canStart = periodUnknown || Boolean(startDate && endDate);

    const swipeStartXRef = useRef(null);
    const swipeStartYRef = useRef(null);
    const showedXapiSentRef = useRef(false);

    const SWIPE_THRESHOLD = 50;

    const sendSelectedXapi = (xapiId) => {
        ServiceUnit_B(xapiId).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
    };

    const closePopup = (closeHandler) => {
        sendSelectedXapi(xAPI_ID_VALUE.serviceUnit.btn_epmintro_0003);
        if (closeHandler) closeHandler();
    };

    const handleSwipeStart = (event) => {
        if (isInteractiveTarget(event.target)) {
            swipeStartXRef.current = null;
            swipeStartYRef.current = null;
            return;
        }

        const pointer = event.touches?.[0] || event;

        swipeStartXRef.current = pointer.clientX;
        swipeStartYRef.current = pointer.clientY;
    };

    const openDatePicker = (event) => {
        const input = event.currentTarget;

        if (input.disabled) return;

        if (typeof input.showPicker !== "function") return;

        // 달력 아이콘이 아닌 입력 영역 어디를 눌러도 달력이 열리도록 처리 (미지원 브라우저는 기본 동작 유지)
        try {
            input.showPicker();
        } catch (error) {}
    };

    const handleSwipeEnd = (event) => {
        if (swipeStartXRef.current === null || swipeStartYRef.current === null) {
            return;
        }

        const pointer = event.changedTouches?.[0] || event;

        const diffX = pointer.clientX - swipeStartXRef.current;
        const diffY = pointer.clientY - swipeStartYRef.current;

        swipeStartXRef.current = null;
        swipeStartYRef.current = null;

        // 세로 스크롤을 가로 스와이프로 잘못 인식하지 않도록 처리
        if (Math.abs(diffX) <= Math.abs(diffY)) {
            return;
        }

        if (Math.abs(diffX) < SWIPE_THRESHOLD) {
            return;
        }

        // GUIDE에서 왼쪽 스와이프
        if (step === POPUP_STEP.GUIDE && diffX < 0) {
            setStep(POPUP_STEP.PERIOD);
            return;
        }

        // PERIOD에서 오른쪽 스와이프
        if (step === POPUP_STEP.PERIOD && diffX > 0) {
            setStep(POPUP_STEP.GUIDE);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            showedXapiSentRef.current = false;
            return;
        }
        if (showedXapiSentRef.current) return;

        showedXapiSentRef.current = true;
        ServiceMessage(xAPI_ID_VALUE.serviceMessage.pop_epmintro_0000).setContent(EXAM_PLAN_XAPI_CONTENT).showed();
    }, [ServiceMessage, isOpen]);

    useEffect(() => {
        if (!isOpen || !missPlanInfo) return undefined;

        let isActive = true;
        setStep(POPUP_STEP.GUIDE);
        setStartDate(toInputDate(missPlanInfo.EXAM_START_DT));
        setEndDate(toInputDate(missPlanInfo.EXAM_END_DT));
        // "시험기간을 몰라요" 는 저장값과 무관하게 팝업이 열릴 때마다 항상 해제 상태로 시작한다.
        setPeriodUnknown(false);
        setExamList([]);
        setWarningMessage("");
        setIsSaving(false);

        if (!mmbrSeq || !missPlanInfo.GRAD_CD) return undefined;

        callPost(
            { user_id: userId },
            {
                REQUEST_API: Constants.MYPAGE_API.EXAM_INFO_LIST,
                HTTP_METHOD: Constants.HTTP_METHOD.GET,
            },
            (res) => {
                if (!isActive) return;
                const nextExamList = getExamList(res);
                const currentExam = nextExamList.find((exam) => exam.EXAM_DVSN_CD === examCode);
                setExamList(nextExamList);
                if (currentExam?.EXAM_START_DT && currentExam?.EXAM_END_DT) {
                    setStartDate(toInputDate(currentExam.EXAM_START_DT));
                    setEndDate(toInputDate(currentExam.EXAM_END_DT));
                }
            },
            { MMBR_SEQ: mmbrSeq, GRAD_CD: missPlanInfo.GRAD_CD, EXAM_DVSN_CD: "" },
        );

        return () => {
            isActive = false;
        };
    }, [examCode, isOpen, missPlanInfo, mmbrSeq, userId]);

    useEffect(() => setWarningMessage(""), [endDate, periodUnknown, startDate]);

    const saveExamSchedule = ({ noExam = false } = {}) => {
        if (!missPlanInfo || !mmbrSeq || isSaving) return;

        const nextPeriodUnknown = periodUnknown || noExam;
        if (!nextPeriodUnknown) {
            const validationMessage = validateExamPeriod(startDate, endDate, examList, examCode);
            if (validationMessage) {
                setWarningMessage(validationMessage);
                return;
            }
        }

        setIsSaving(true);

        if (missPlanInfo.PLAN_SEQ !== null && missPlanInfo.PLAN_SEQ !== undefined) {
            const missionPlanBody = createMissionPlanPayload({
                startDate,
                endDate,
                periodUnknown,
                noExam,
            });
            callPost(
                { user_id: userId },
                {
                    REQUEST_API: Constants.MID_STUDY_API.MISSION_PLAN_INFO,
                    HTTP_METHOD: Constants.HTTP_METHOD.PUT,
                    BODY: missionPlanBody,
                },
                null,
                { MMBR_SEQ: mmbrSeq, PLAN_SEQ: missPlanInfo.PLAN_SEQ },
            );
        }

        callPost(
            { user_id: userId },
            {
                REQUEST_API: Constants.MYPAGE_API.EXAM_INFO_SAVE,
                HTTP_METHOD: Constants.HTTP_METHOD.POST,
                BODY: createExamSchedulePayload({
                    missPlanInfo,
                    mmbrSeq,
                    startDate,
                    endDate,
                    periodUnknown: nextPeriodUnknown,
                    noExam,
                }),
            },
            (res) => {
                setIsSaving(false);
                if (isSaveSuccessful(res)) {
                    /**
                     * "이번에는 시험을 보지 않아요"(noExam)는 미참여(JOIN_YN: N)로 저장된다.
                     * 홈의 안내 팝업 재노출 차단 조건은 참여(JOIN_YN === "Y")뿐이라 그냥 닫으면
                     * 다음 진입에서 또 뜬다. '오늘은 그만 볼래요'와 같은 경로로 닫아 당일은 다시 띄우지 않는다.
                     */
                    const closeHandler = (noExam && onCloseToday) || onClose;

                    if (closeHandler) closeHandler();
                    if (onComplete) onComplete();
                    return;
                }
                AlertConfirmPopup({
                    type: "ALERT",
                    msg: "시험 일정 저장 중 문제가 발생했습니다.<br />다시 시도해 주세요.",
                });
            },
            { MMBR_SEQ: mmbrSeq },
        );
    };

    const handlePeriodUnknown = () => {
        sendSelectedXapi(xAPI_ID_VALUE.serviceUnit.btn_epmintro_0000);
        const nextPeriodUnknown = !periodUnknown;
        setPeriodUnknown(nextPeriodUnknown);
        if (nextPeriodUnknown) {
            setStartDate("");
            setEndDate("");
        }
    };

    const handleSkipExam = () => {
        if (isSaving) return;

        sendSelectedXapi(xAPI_ID_VALUE.serviceUnit.btn_epmintro_0002);

        AlertConfirmPopup({
            type: "CUSTOM",
            msg: "시험 일정이 없으면<br />시험대비모드를 이용할 수 없어요.<br /><br />시험대비모드 없이 계속 진행할까요?",
            buttonInfo: [
                { title: "네, 계속할게요", value: "continue" },
                { title: "아니요, 날짜 입력할게요", value: "input" },
            ],
        }).then((answer) => {
            if (answer === "continue") {
                sendSelectedXapi(xAPI_ID_VALUE.serviceUnit.btn_epmintro_0004);
                saveExamSchedule({ noExam: true });
            }
        });
    };
    if (!missPlanInfo) return null;

    return (
        <Modal
            isOpen={Boolean(isOpen)}
            backdrop="static"
            keyboard={false}
            modalClassName="expln-popup-modal"
            className={`expln-popup ${step === POPUP_STEP.SUMMARY ? "expln-popup--summary" : ""}`}
        >
            {step === POPUP_STEP.SUMMARY && (
                <>
                    <div className="expln-popup__body expln-popup__body--summary">
                        <img
                            className="expln-popup__summary-img"
                            src={examPlanInfoImage}
                            alt="시험기간 등록, 미션 수행, 성적 입력으로 진행하는 시험대비모드 안내"
                        />
                        <span className="expln-popup__summary-day">{Math.abs(missPlanInfo.START_D_DAY)}</span>
                        <span className="expln-popup__summary-date">{missPlanInfo.FIRST_ACTION_DT}</span>
                    </div>
                    <footer className="expln-popup__foot">
                        <button
                            type="button"
                            className="expln-popup__today-close"
                            onClick={() => closePopup(onCloseToday || onClose)}
                        >
                            오늘은 그만 볼래요
                        </button>
                        <button type="button" className="expln-popup__next" onClick={() => closePopup(onClose)}>
                            닫기
                        </button>
                    </footer>
                </>
            )}

            {step === POPUP_STEP.GUIDE && (
                <div
                    className="expln-popup__body expln-popup__body--guide"
                    onPointerDown={handleSwipeStart}
                    onPointerUp={handleSwipeEnd}
                >
                    <p className="expln-popup__guide-label">
                        {missPlanInfo.GRAD_NM} {missPlanInfo.SMST_NM}
                    </p>
                    <button type="button" className="expln-popup__primary" onClick={() => setStep(POPUP_STEP.PERIOD)}>
                        다음
                    </button>

                    <div className="expln-popup__dots">
                        <span className="is-active" />
                        <span />
                    </div>
                </div>
            )}

            {step === POPUP_STEP.PERIOD && (
                <div
                    className="expln-popup__body expln-popup__body--period"
                    onPointerDown={handleSwipeStart}
                    onPointerUp={handleSwipeEnd}
                >
                    <button
                        type="button"
                        className="expln-popup__close"
                        onClick={() => closePopup(onCloseToday || onClose)}
                        disabled={isSaving}
                    >
                        닫기
                    </button>
                    <p className="expln-popup__guide-label">
                        {missPlanInfo.GRAD_NM} {missPlanInfo.SMST_NM}
                    </p>

                    <div className="expln-popup__period-box">
                        <div className="expln-popup__period-box-tit">
                            <p>나의 시험 기간 입력하기</p>
                            <label className="expln-popup__check">
                                <input type="checkbox" checked={periodUnknown} onChange={handlePeriodUnknown} />
                                <span>시험기간을 몰라요</span>
                            </label>
                        </div>

                        <div className={`expln-popup__date-box ${warningMessage ? "has-warning" : ""}`}>
                            <label className="expln-popup__date-field">
                                <input
                                    type="date"
                                    value={startDate}
                                    disabled={periodUnknown}
                                    onClick={openDatePicker}
                                    onChange={(event) => setStartDate(event.target.value)}
                                />
                            </label>
                            <span className="expln-popup__date-separator">~</span>
                            <label className="expln-popup__date-field">
                                <input
                                    type="date"
                                    value={endDate}
                                    disabled={periodUnknown}
                                    onClick={openDatePicker}
                                    onChange={(event) => setEndDate(event.target.value)}
                                />
                            </label>
                        </div>
                        {warningMessage && <p className="expln-popup__warning">{warningMessage}</p>}
                        <button
                            type="button"
                            className="expln-popup__start"
                            disabled={!canStart || isSaving}
                            onClick={() => {
                                sendSelectedXapi(xAPI_ID_VALUE.serviceUnit.btn_epmintro_0001);
                                saveExamSchedule();
                            }}
                        >
                            {isSaving ? "저장 중..." : "시험대비모드 시작"}
                        </button>
                        <label className="expln-popup__skip">
                            {/* <input type="checkbox" checked={false} onChange={handleSkipExam} disabled={isSaving} /> */}
                            <span onClick={handleSkipExam}>이번에는 시험을 보지 않아요</span>
                        </label>
                    </div>

                    <div className="expln-popup__dots">
                        <span />
                        <span className="is-active" />
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default React.memo(ExamPlanIntroPopup);
